import type { ReactNode } from 'react';
import type { MetricsBlock, RegionRow, UnattributedBooks } from '../../api/types.js';
import { DataTable, type Column } from '../DataTable/DataTable.js';
import { DataQualityBadge } from '../DataQualityBadge/DataQualityBadge.js';
import { MetricCell, TREND_SLOT_PX, formatValue } from '../MetricCell/MetricCell.js';
import { breakdownRows, metricValue } from '../Breakdown/Breakdown.js';
import { RegionCell } from '../RegionCell/RegionCell.js';

/**
 * The Region Performance table (FR-032, FR-033, PRD §6.2, §9.8).
 *
 * All sixteen FR-033 columns, plus **MGR** — which the blueprint's table and PRD §6.2 both
 * carry. MGR has no source column yet (§9.8.4), so it renders `—`, never `0`: a zero would
 * assert the region has no managers, when the truth is the data is not there. It is included
 * rather than omitted because Principle I makes the blueprint the authority on what the table
 * looks like, and its absence would leave a visible gap in the grid.
 *
 * **Two columns carry two figures each**, and they are the only two: `#JOBS` renders
 * `jobCount(openingsSum)` — e.g. `17(27)` (FR-033) — and since 2026-09-08 `#SUBS` renders
 * `subs(uniqueSubs)`, the submissions in the period beside the distinct candidates behind them
 * (FR-084, PRD §9.8.1a). Both therefore need their own track width, and both keep the space
 * before the parenthetical in the accessible name while dropping it from the visible face — see
 * `<MetricCell>` for why those two diverge.
 *
 * Filtering is applied **client-side** over `metricsByCombo`, which the API precomputes for
 * every combination. That is why the overview request carries only a period: the numbers for
 * every filter state are already in the response, so switching Source or Specialty is instant
 * and cannot disagree with what the server would have returned.
 */

/**
 * The geometry of a metric track, derived once rather than measured a third time.
 *
 * `<MetricCell>` clips (`overflow: hidden`) and its face is `white-space: nowrap`, so a face
 * wider than its content box is not overlapped — since 2026-08-30 it is **silently cut**, which
 * is what was reported at 24M: `119.77K(131.28K)` rendering as `119.77K(131.2`. Nothing on screen
 * says a digit is missing, which is the whole reason these numbers are pinned here.
 *
 * Everything below follows from three constants and one fact about the typeface:
 *
 * - IBM Plex Mono is a fixed **600/1000-em** advance, and the face is `fontSize.captionStrong`
 *   (11.5px) — so every glyph is exactly **6.9px**, whatever the figure. `tabular-nums` on
 *   `.mono` keeps the fallbacks in step.
 * - `TREND_SLOT_PX` in `<MetricCell>` reserves **36px** — the arrow and its 30-day percentage,
 *   `▲12%` — on every cell of a column that draws trends, so a row with no delta is the same width
 *   as one with. Until 2026-09-27 it was an 8px arrow alone, and the columns that never trend still
 *   carry those 8px in their budget below (`ARROW_SLOT_PX`): they draw nothing there any more, and
 *   the slack is left to the data-quality badge beside BOB, PEND, PROS and PEMP rather than
 *   re-derived.
 * - The cell's chrome is **20px**: `<DataTable>`'s `0 6px` on the grid cell and `<MetricCell>`'s
 *   own `0 4px` inside it.
 *
 * ⚠ The glyph COUNT is bounded by the formatter, not by the data, which is what makes these
 * widths safe to state rather than re-measure every ingest. `formatNumber` compacts at 1,000 to
 * exactly two decimals, so no figure can ever exceed seven glyphs (`999.99K`, `123.45M`) and no
 * two-figure face can exceed sixteen (`999.99K(999.99K)`). The 2026-08-30 and 2026-09-08 fixes
 * both sized to the widest face *then in the snapshot* and both were overtaken by the next one —
 * `#JOBS` reaches all sixteen glyphs on the promoted snapshot today.
 */
const GLYPH_PX = 6.9;
const ARROW_SLOT_PX = 8;
const CELL_CHROME_PX = 20;
const track = (glyphs: number, slot: number = ARROW_SLOT_PX) =>
  Math.ceil(glyphs * GLYPH_PX + slot + CELL_CHROME_PX);

/**
 * The columns that can carry a trend — the only metrics the ingest runs `periodTrend` or
 * `stockTrend` over (`regionTable.ts`); every other column is a `flatMetric` and its `d30` is
 * always null.
 *
 * ⚠ **Only these reserve the trend slot**, and it is a width decision, not a styling one: 36px on
 * all fourteen metric columns would be ~390px of reserved emptiness across the row, where on these
 * five it is 140px. A column added to the ingest's trended set has to join this list, or its
 * trend exists in the panel and never on the face.
 */
const TRENDED_KEYS: ReadonlySet<string> = new Set([
  'jobCount',
  'subs',
  'offers',
  'starts',
  'activeToa',
]);

/**
 * The two columns that carry two figures — `#JOBS` and `#SUBS` — at the widest face the formatter
 * can produce: **139px**.
 *
 * The blueprint's metric track is `minmax(50px, 1fr)`, which the `1fr` distribution renders at
 * ~76px — 56px of content box, which is a single figure and nothing else. Measured on the
 * promoted snapshot `20260908T135416Z`, across all 84 filter/period combinations and the totals
 * row:
 *
 * | column         | widest face at 24M  | ink     | at the old 116px floor |
 * | -------------- | ------------------- | ------- | ---------------------- |
 * | `#JOBS`        | `119.77K(131.28K)`  | 118.4px | cut by 22.4px          |
 * | `#SUBS` totals | `41.35K(12.86K)`    | 104.6px | cut by 8.6px           |
 * | `#SUBS` region | `13.54K(3.49K)`     | 97.7px  | cut by 1.7px           |
 *
 * ⚠ Sized to the **formatter's** sixteen-glyph ceiling, not to that 118.4px, and the two happen
 * to be the same number today only because `#JOBS` already reaches the ceiling. Sizing to the
 * table is what needed redoing twice.
 *
 * Still `1fr` above the floor, so the two share the row's spare width like their siblings and
 * neither widens the table on a screen that has room. Pixels rather than `minmax(min-content, …)`
 * because `<DataTable>` sets `minWidth: 0` on every cell, which zeroes the min-content
 * contribution — that was tried first and moved the gap 4px → 8px.
 */
export const TWO_FIGURE_TRACK_PX = track(16);
export const TWO_FIGURE_TRACK = `minmax(${TWO_FIGURE_TRACK_PX}px, 1fr)`;

/**
 * The same two-figure face with the trend beside it — `119.77K(131.28K)▲12%`: **167px**
 * (2026-09-27). Used wherever the column draws its trend; `TWO_FIGURE_TRACK` stays the width on
 * MTD/YTD, where the face carries no trend and the table goes back to exactly what it was.
 */
export const TRENDED_TWO_FIGURE_TRACK_PX = track(16, TREND_SLOT_PX);
export const TRENDED_TWO_FIGURE_TRACK = `minmax(${TRENDED_TWO_FIGURE_TRACK_PX}px, 1fr)`;

/**
 * A single-figure column with its trend — `20.10K▲12%`: **98px**, against the ~70px a sibling is
 * budgeted. Six glyphs, the same measured bound the sibling term below uses, so it goes stale on
 * the same day that one does.
 */
export const TRENDED_TRACK_PX = track(6, TREND_SLOT_PX);
const TRENDED_TRACK = `minmax(${TRENDED_TRACK_PX}px, 1fr)`;

/**
 * The table's own horizontal minimum — **wider than `layout.contentMinWidth`**, and it has to be.
 *
 * The row is `1fr`-distributed, so widening two tracks does not widen the table: it takes the
 * space off the twelve siblings. At the shared 1180px floor that leaves them 62.5px against the
 * 69.4px `BOB`'s `20.10K` needs, so fixing `#JOBS` by itself would have started cutting the
 * single-figure columns instead — a smaller cut, in more places, which is a worse trade and not
 * one the reader can see either.
 *
 * `60 + 46 + 46 + 2×139 + 12×70 = 1270` — on MTD/YTD, where no trend is drawn. On the trailing
 * periods the two pairs and the three trended singles carry the trend slot (2026-09-27), so it is
 * `60 + 46 + 46 + 2×167 + 3×98 + 9×70 = 1410`, plus the action column either way. Below this the table scrolls sideways rather than
 * clipping a figure, which is the right way round for a page of numbers: `<ClientBreakdown>`
 * already declares its own wider minimum (1260px) for the same reason.
 *
 * ⚠ The sibling term is the widest single figure **in the snapshot** (`20.10K`, six glyphs), not
 * the formatter's seven-glyph ceiling — the honest bound is `track(7)` = 77px each and a 1345px
 * table, which would put every laptop into horizontal scroll to insure against a magnitude no
 * column is near. So this one number is a measurement and can go stale: if any single-figure
 * column reaches **100.00K**, it gains a glyph and this needs 83px more.
 */
/**
 * The per-row export control's track (FR-090).
 *
 * ⚠ **An action column fits HERE where it did not fit in the chart's matrix**, and the difference
 * is not the number of pixels — it is what each table has already decided about scrolling. The
 * matrix is pinned under ~1,082px of card interior by a 2026-09-10 owner decision and a test, so
 * six extra pixels there restore a scrollbar that was deliberately removed. This table already
 * declares 1,270px against a 1,180px page floor and already scrolls sideways below it, on purpose
 * — "the right way round for a page of numbers". So the track moves it to 1,338px, still inside
 * the 1,345px honest bound the note above describes, and changes nothing about when it scrolls.
 *
 * ⚠ **68px, not the 40px the control needs, because the column carries a NAME** (owner request
 * 2026-09-14). It shipped with `label: ''` — a headerless track, which is what a column of icons
 * can get away with only while nothing else in the row is nameless. `Action` at
 * `fontSize.tableHeader` (9px/700, .2px tracking) is ~33px of ink, and the header cell spends
 * 10px of padding on the left and 20px on the right as the last column, so 63px is the floor and
 * 68px is it with the slack a font substitution needs. ⚠ The header sets `whiteSpace: nowrap`,
 * so a track below that floor does not wrap the word — it clips it.
 */
const ACTION_COLUMN_WIDTH = 68;

const TABLE_MIN_WIDTH = `${60 + 46 + 46 + 2 * track(16) + 12 * track(6) + ACTION_COLUMN_WIDTH}px`;
const TRENDED_TABLE_MIN_WIDTH = `${
  60 + 46 + 46 + 2 * TRENDED_TWO_FIGURE_TRACK_PX + 3 * TRENDED_TRACK_PX + 9 * track(6) +
  ACTION_COLUMN_WIDTH
}px`;

export interface RegionTableProps {
  rows: RegionRow[];
  totals: MetricsBlock;
  combo: string;
  onSelectRegion: (regionCode: string) => void;
  /**
   * Renders the per-row export control, when the page supplies one (FR-090).
   *
   * ⚠ **A render prop, not a `regionCode` callback.** The control is a `<Disclosure>` that owns
   * streaming progress, a consent round trip and a completion dialog — state this table has no
   * business holding, and which must not reset when a sort re-renders the rows. Passing the node
   * in keeps that with the page, and keeps `<RegionTable>` a table.
   *
   * Omitted, the column does not exist at all — so the Cards view, Region Detail's copy and any
   * future caller are unchanged rather than gaining an empty track.
   */
  onExportRegion?: (regionCode: string) => ReactNode;
  unattributedBooks: UnattributedBooks | null | undefined;
  /**
   * The section's title and filters, rendered **inside** the table's own card.
   *
   * Forwarded to `<DataTable header>` for the reason that prop exists: a header floating above
   * a separate card reads as two unrelated blocks. Passing it in makes the table one panel.
   */
  header?: ReactNode;
  /**
   * Whether the cells draw their trend arrows. Defaults to `true`.
   *
   * The page passes `showsTrendArrows(period)`: `false` on MTD and YTD, where the figure is a
   * partial calendar span and the arrow is a fixed 30-day trailing comparison, so the two
   * describe different windows (PRD §9.5, amended 2026-08-28). It suppresses the **glyph only** —
   * both trend figures stay in the tooltip and the accessible description, which is why the
   * table can withhold the summary without withholding anything a reader could act on.
   */
  showTrendArrows?: boolean;
  /**
   * The regions the chart above is scoped to, washed amber in the rows (blueprint
   * `rowBg: chartRegion===r.code ? '#fff9d6'`).
   *
   * The chart and the table are deliberately separate state (FR-034) and this does not change
   * that: it moves no figure, only says which rows the line belongs to. Without it the pills
   * narrow a chart of unlabelled lines while sixteen columns of numbers sit underneath with
   * nothing tying the two together.
   *
   * A **list**, where the blueprint has a single code, because the pills multi-select and chart
   * their sum — so every selected region is marked, not just the last one clicked. Empty means
   * all six, which is the page's default scope, so it highlights **nothing**: a wash on every
   * row marks no row.
   */
  selectedRegionCodes?: readonly string[];
}

interface Row {
  region: RegionRow;
  metrics: MetricsBlock;
  /** The same row under one filter axis varied — the hover breakdown's source. */
  siblings: {
    internal?: MetricsBlock;
    target?: MetricsBlock;
    nursing?: MetricsBlock;
    allied?: MetricsBlock;
  };
}

export function RegionTable({
  rows,
  totals,
  combo,
  onSelectRegion,
  onExportRegion,
  unattributedBooks,
  header,
  showTrendArrows = true,
  selectedRegionCodes = [],
}: RegionTableProps) {
  // The displayed combo, plus the three single-axis siblings the hover breakdown decomposes
  // into. Varying one axis at a time keeps every row a decomposition of the cell on screen
  // rather than of some unfiltered reference (blueprint § tooltip).
  const [source, targeting, specialty] = combo.split('|');
  const siblingKeys = {
    internal: `internal|${targeting}|${specialty}`,
    target: `${source}|target|${specialty}`,
    nursing: `${source}|${targeting}|nursing`,
    allied: `${source}|${targeting}|allied`,
  };

  const tableRows: Row[] = rows.map((region) => ({
    region,
    metrics: region.metricsByCombo?.[combo] ?? region.metrics ?? {},
    siblings: {
      internal: region.metricsByCombo?.[siblingKeys.internal],
      target: region.metricsByCombo?.[siblingKeys.target],
      nursing: region.metricsByCombo?.[siblingKeys.nursing],
      allied: region.metricsByCombo?.[siblingKeys.allied],
    },
  }));

  const metricColumn = (
    key: string,
    label: string,
    fullLabel: string,
    options: {
      bold?: boolean;
      dataQuality?: boolean;
      breakdown?: false | 'specialtyOnly';
    } = {},
  ): Column<Row> => ({
    key,
    label,
    fullLabel,
    ...(showTrendArrows && TRENDED_KEYS.has(key) ? { width: TRENDED_TRACK } : {}),
    /*
     * CENTRED, not right-aligned (owner request 2026-08-30) — header and figures together, so each
     * numeric column reads as one block under its own label.
     *
     * The trade, recorded because it is real: right alignment lines the digits of a column up
     * vertically, and centring does not — `1.52K` and `515` centred put their decimal points in
     * different places. What makes it work here is that the trend occupies a reserved slot
     * (`TREND_SLOT_PX` in `<MetricCell>`); without that, a centred figure would also shift by half an
     * arrow's width depending on whether its row happened to have a delta.
     */
    align: 'center',
    render: (row) => (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
        <MetricCell
          metric={row.metrics[key]}
          align="center"
          label={fullLabel}
          metricKey={key}
          bold={options.bold}
          showTrendArrow={showTrendArrows && TRENDED_KEYS.has(key)}
          breakdown={
            options.breakdown === false
              ? undefined
              : breakdownRows({
                  total: metricValue(row.metrics, key),
                  internal: metricValue(row.siblings.internal, key),
                  target: metricValue(row.siblings.target, key),
                  nursing: metricValue(row.siblings.nursing, key),
                  allied: metricValue(row.siblings.allied, key),
                  // Book and job columns do not take the source or targeting axes (§9.8.2), so
                  // an Internal/Target row there would restate the total under another name.
                  includeSource: options.breakdown !== 'specialtyOnly',
                  includeTargeting: options.breakdown !== 'specialtyOnly',
                })
          }
        />
        {options.dataQuality ? (
          <DataQualityBadge unattributedBooks={unattributedBooks} figureLabel={fullLabel} />
        ) : null}
      </div>
    ),
    sortValue: (row) => row.metrics[key]?.value ?? null,
    renderTotal: () => (
      <MetricCell
        metric={totals[key]}
        align="center"
        label={`Total ${fullLabel}`}
        metricKey={key}
        bold
        showTrendArrow={showTrendArrows && TRENDED_KEYS.has(key)}
      />
    ),
  });

  const columns: Array<Column<Row>> = [
    {
      key: 'region',
      label: 'REGION',
      fullLabel: 'Region — code shown, full name on hover or focus',
      width: '60px',
      align: 'left',
      render: (row) => (
        <RegionCell
          regionCode={row.region.regionCode}
          regionName={row.region.regionName}
          isSpecial={row.region.isSpecial}
        />
      ),
      sortValue: (row) => row.region.regionCode,
    },
    { ...metricColumn('leaderCount', 'LDR', 'Leaders', { breakdown: false }), width: '46px' },
    // No source column yet (§9.8.4) — renders `—`, and nothing else depends on it.
    {
      ...metricColumn('managerCount', 'MGR', 'Managers — pending source column', {
        breakdown: false,
      }),
      width: '46px',
    },
    metricColumn('recruiters', 'REC', 'Recruiters', { bold: true, breakdown: 'specialtyOnly' }),
    metricColumn('recruitersNursing', 'REC/N', 'Recruiters — Nursing', { breakdown: false }),
    metricColumn('recruitersAllied', 'REC/A', 'Recruiters — Allied', { breakdown: false }),
    metricColumn('targetAccounts', 'TRGT', 'Target Accounts (Primary + Secondary)', {
      breakdown: false,
    }),
    {
      key: 'jobCount',
      label: '#JOBS',
      fullLabel: 'Jobs (Openings)',
      // Centred with the rest of the numeric columns — see the note in `metricColumn`.
      align: 'center',
      /*
       * Two figures in one cell, so it takes `TWO_FIGURE_TRACK` — see the derivation there.
       *
       * `jobCount(openingsSum)` plus a trend arrow (FR-033). It is the column that found this
       * twice: reported 2026-08-30 as "the columns are so close, I can't tell which number
       * belongs to which" (at 116px it spilled left into TRGT), and again 2026-09-08 as figures
       * "getting cropped" at 24M — the same overrun, only now cut off instead of overlapping,
       * because `<MetricCell>` gained `overflow: hidden` in between. It is the widest face in the
       * table: `119.77K(131.28K)`, all sixteen glyphs the formatter can produce.
       *
       * The figures here are post-2026-08-30, when the space before the parenthetical was dropped
       * from the visible face (`164 (176)` → `164(176)`). That returned a glyph. If the space
       * ever comes back, the track is `track(17)`.
       */
      width: showTrendArrows ? TRENDED_TWO_FIGURE_TRACK : TWO_FIGURE_TRACK,
      render: (row) => (
        <MetricCell
          metric={row.metrics.jobCount}
          align="center"
          label="Jobs (Openings)"
          metricKey="jobCount"
          showTrendArrow={showTrendArrows}
          suffix={
            row.metrics.openingsSum?.value != null
              ? `(${formatValue(row.metrics.openingsSum.value)})`
              : undefined
          }
          /*
           * Total · Target · Nursing · Allied. **No Internal/External** (owner decision
           * 2026-08-30): the question this column answers is "how much is open, and how much of
           * that is at a target account". Whether the client sits in this region's own book is a
           * different question and not one being asked of jobs.
           *
           * The rows were briefly on, because the geographic fallback (§9.1.1) made the Internal
           * test non-trivial for jobs for the first time — it can now separate. Being able to
           * separate is not the same as being wanted, and this panel is four rows because four is
           * what a reader needs.
           *
           * `total === nursing + allied` here, since 2026-08-30: `jobsIn` counts the two clinical
           * halves only, so the parts add up to the headline above them. Before that the panel
           * showed a 277–609 job remainder per region with nothing on screen to explain it.
           */
          breakdown={breakdownRows({
            total: metricValue(row.metrics, 'jobCount'),
            target: metricValue(row.siblings.target, 'jobCount'),
            nursing: metricValue(row.siblings.nursing, 'jobCount'),
            allied: metricValue(row.siblings.allied, 'jobCount'),
            includeSource: false,
          })}
        />
      ),
      sortValue: (row) => row.metrics.jobCount?.value ?? null,
      renderTotal: () => (
        <MetricCell
          metric={totals.jobCount}
          align="center"
          label="Total Jobs (Openings)"
          metricKey="jobCount"
          bold
          showTrendArrow={showTrendArrows}
          suffix={
            totals.openingsSum?.value != null
              ? `(${formatValue(totals.openingsSum.value)})`
              : undefined
          }
        />
      ),
    },
    {
      key: 'subs',
      label: '#SUBS',
      fullLabel: 'Total Subs (Unique)',
      align: 'center',
      /*
       * The second column carrying two figures, so it takes the identical `TWO_FIGURE_TRACK`
       * (2026-09-08): `subs(uniqueSubs)` plus a trend arrow.
       *
       * ⚠ It is **not** as wide as `#JOBS` today — `41.35K(12.86K)` on the totals row against
       * `119.77K(131.28K)` — and it still takes the same track, because the two are bounded by
       * the same formatter and a per-column measurement is what had to be redone twice. Below the
       * floor the widest face overruns the ~76px a blueprint-default column gets, and since the
       * columns are centred it spills **both** ways: into `#JOBS`'s pair on the left and `OFRS`
       * on the right.
       *
       * `tests/regiontable.columns.test.tsx` predicted this arrival — "if a second column ever
       * gains a suffix, it needs the same treatment" — and its thirteen-column assertion is what
       * failed to say so.
       */
      width: showTrendArrows ? TRENDED_TWO_FIGURE_TRACK : TWO_FIGURE_TRACK,
      render: (row) => (
        <MetricCell
          metric={row.metrics.subs}
          align="center"
          label="Total Subs (Unique)"
          metricKey="subs"
          showTrendArrow={showTrendArrows}
          suffix={
            row.metrics.uniqueSubs?.value != null
              ? `(${formatValue(row.metrics.uniqueSubs.value)})`
              : undefined
          }
          /*
           * Total · Internal · External · Target · Nursing · Allied — the full breakdown this
           * column has always had, decomposing `subs` ALONE.
           *
           * ⚠ **The unique figure is deliberately absent from the panel, where the blueprint's
           * own cell repeats it on every row.** Unique counts do not decompose: a candidate
           * submitted to both a Nursing and an Allied job inside the window counts in both
           * halves, so `nursing + allied` overshoots the headline — measured 4 at 90d, 10 at 12M,
           * 30 at 24M. A panel printing `6.51K(3.60K)` over `4.09K(2.06K)` under a headline of
           * `44.18K(10.56K)` invites the reader to add the parentheticals, and this product has
           * already been reported twice for parts that do not reconcile with their whole
           * (G-28 for `#JOBS`, FR-071 for `#SUBS`). The face carries the pair; the panel answers
           * for the figure that adds up.
           */
          breakdown={breakdownRows({
            total: metricValue(row.metrics, 'subs'),
            internal: metricValue(row.siblings.internal, 'subs'),
            target: metricValue(row.siblings.target, 'subs'),
            nursing: metricValue(row.siblings.nursing, 'subs'),
            allied: metricValue(row.siblings.allied, 'subs'),
          })}
        />
      ),
      sortValue: (row) => row.metrics.subs?.value ?? null,
      renderTotal: () => (
        <MetricCell
          metric={totals.subs}
          align="center"
          metricKey="subs"
          /*
           * `Total ${fullLabel}` — written as the expression the shared `metricColumn()` helper
           * uses rather than spelled out, because spelled out it reads as a typo: the blueprint's
           * own full label already begins with "Total", so this announces "Total Total Subs
           * (Unique)". Byte-identical to what this column announced before it gained a second
           * figure, and distinct from the body cells' name, which is what a reader tabbing the
           * table needs. Rewording it is a change to every totals cell in the table, not to this
           * one.
           */
          label={`Total ${'Total Subs (Unique)'}`}
          bold
          showTrendArrow={showTrendArrows}
          suffix={
            totals.uniqueSubs?.value != null
              ? `(${formatValue(totals.uniqueSubs.value)})`
              : undefined
          }
        />
      ),
    },
    metricColumn('offers', 'OFRS', 'Offers'),
    metricColumn('starts', 'STRTS', 'Starts'),
    metricColumn('bob', 'BOB', 'Total Candidates in BoB', {
      bold: true,
      dataQuality: true,
      breakdown: 'specialtyOnly',
    }),
    metricColumn('activeToa', 'ACTV', 'Active TOA'),
    /*
     * ⚠ **ONB carries no data-quality badge and takes the full breakdown** — unlike the four
     * book columns around it, and unlike itself before 2026-09-03.
     *
     * It reads run rate now (booked, not yet started), so it is an ACTV-shaped column: an
     * attribution shortfall in the *book* cannot under-report it, and the Internal/Target rows
     * of its hover panel are real narrowings rather than the total restated under two more
     * names. Restoring either option here re-asserts a lineage the figure no longer has.
     */
    metricColumn('onboarding', 'ONB', 'Onboarding'),
    // PEND, PROS and PEMP are all derived from the same book pass as BOB (§9.8.3), so a
    // shortfall in attribution under-reports each of them too (FR-064).
    metricColumn('pending', 'PEND', 'Pending Candidates', {
      dataQuality: true,
      breakdown: 'specialtyOnly',
    }),
    metricColumn('prospect', 'PROS', 'Prospect Candidates', {
      dataQuality: true,
      breakdown: 'specialtyOnly',
    }),
    metricColumn('prevEmployed', 'PEMP', 'Previously Employed', {
      dataQuality: true,
      breakdown: 'specialtyOnly',
    }),
    ...(onExportRegion
      ? [
          {
            key: 'actions',
            label: 'Action',
            fullLabel: 'Action — export this region to a Google Sheet',
            align: 'center' as const,
            width: `${ACTION_COLUMN_WIDTH}px`,
            sortable: false,
            /*
             * ⚠ The totals row has no region to export, so it renders nothing rather than a
             * disabled control: "export the sum of seven overlapping sets" is not a question this
             * feature answers, and the toolbar button above the table is what covers all regions.
             */
            render: (row: Row) =>
              row.region.regionCode ? (
                /*
                 * ⚠ **The row is itself a button, so anything interactive inside it has to stop
                 * the event escaping.** `<DataTable>` puts `onClick` and an Enter/Space
                 * `onKeyDown` on the row `div` to open the region's detail page, so without this
                 * the export control fired *and* navigated away — the click bubbling straight
                 * past it into the row.
                 *
                 * ⚠ **The completion dialog is inside this subtree too**, `position: fixed`
                 * notwithstanding — fixed positioning moves where a node paints, never where its
                 * events go. So "Open sheet", "Close" and the scrim all bubbled here as well:
                 * finishing an export and clicking the link would have navigated the page out
                 * from under the reader. Wrapping the whole control, rather than the trigger,
                 * is what covers that.
                 *
                 * ⚠ **Only Enter and Space are stopped, never every key.** React's
                 * `stopPropagation` calls the native one, so blanket-stopping `keydown` would
                 * also keep Escape from reaching the `window` listener that closes the dialog.
                 * Those two are exactly what the row listens for.
                 */
                <div
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
                  }}
                  style={{ display: 'flex', justifyContent: 'center' }}
                >
                  {onExportRegion(row.region.regionCode)}
                </div>
              ) : null,
          },
        ]
      : []),
  ];

  return (
    <DataTable<Row>
      caption="Region Performance"
      header={header}
      columns={columns}
      rows={tableRows}
      rowKey={(row) => row.region.regionCode}
      // Wider than the shared `layout.contentMinWidth` — see `TABLE_MIN_WIDTH`. Sixteen columns,
      // two of them carrying two figures, do not fit the blueprint's 1180px without something
      // being cut, and a sideways scroll is the honest failure of the two.
      minWidth={showTrendArrows ? TRENDED_TABLE_MIN_WIDTH : TABLE_MIN_WIDTH}
      onRowClick={(row) => onSelectRegion(row.region.regionCode)}
      isRowSelected={(row) => selectedRegionCodes.includes(row.region.regionCode)}
      showTotals
      emptyMessage="No regions in this snapshot."
      // The card is one of the page's two subjects, so it wears the featured edge — the same one
      // `PANEL_STYLE` gives the chart above it and the header card the Cards view falls back to
      // (owner request 2026-08-30). All three states of this panel must match.
      featured
    />
  );
}
